import { AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../../utils/api'
import { filteredFilters } from '../../../utils/helpers'
import { downloadExportViaToken, waitForConfigLoad } from '../../../utils/sagaUtils'
import { Dictionary } from '../../../utils/types'
import { getAccountImportsPageSize, getAccountReportsPageSize, getCountryCode } from '../../config'
import { withLoading } from '../../loading'
import { ColumnSorting, TableActions, sortParam } from '../../table'
import { selectSorting } from '../../table/selectors'
import { getVisibleDialogData } from '../../dialog/selectors'
import { DialogActions } from '../../../redux/dialog'
import { DialogType } from '../../../redux/dialog/types'

import { ImportHistoryActions } from './actions'
import { getAccountImportsFilter, getAccountImportsFilters } from './selectors'
import { Import } from './types'

import { defaultStatusFilters, mapUserStatusToValue } from '.'

type ImportHistoryResponse = {
  data: {
    imports: Import[]
  }
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

const preprocessFilters = (filters: Dictionary<any>) => {
  return _.mapValues(
    {
      ...filters,
      lastStep: filters.lastStep ? mapUserStatusToValue(filters.lastStep) : defaultStatusFilters
    },
    filter => (_.has(filter, 'value') ? _.get(filter, 'value') : filter)
  )
}

export function* exportImports({ payload }: ReturnType<typeof ImportHistoryActions.exportImports>) {
  yield call(waitForConfigLoad)

  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'importHistory' })
  const filter: string = yield select(getAccountImportsFilter)
  const filters: Dictionary<any> = yield select(getAccountImportsFilters)
  const nonEmptyFilters = filteredFilters(filters)
  const preprocessedFilters = preprocessFilters(nonEmptyFilters)

  try {
    yield call(apiCall, {
      url: '/api/import/export',
      method: 'post',
      params: {
        domainCode: countryCode,
        page: 1,
        pageSize: 1,
        order: sortParam(sorting),
        search: filter || undefined
      },
      data: {
        name: payload.name,
        filter: _.every(_.values(preprocessedFilters), _.isEmpty) ? {} : preprocessedFilters,
        columns: _.map(payload.columns, x => ({ id: x.id, label: x.label }))
      }
    })
  } catch (e) {
    const visibleDialog: DialogType = yield select(getVisibleDialogData)
    if (visibleDialog === 'ImportHistoryExport')
      yield put(
        DialogActions.updateDialogData({
          dialog: 'ImportHistoryExport',
          data: {
            isError: true
          }
        })
      )
    else {
      yield put(
        DialogActions.showDialogWithData({
          dialog: 'ImportHistoryExport',
          data: {
            isError: true
          }
        })
      )
    }
  }
}

export function* fetchImports(action: ReturnType<typeof ImportHistoryActions.fetchData>) {
  yield call(waitForConfigLoad)
  const { payload } = action

  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'importHistory' })
  const filter: string = yield select(getAccountImportsFilter)
  const filters: Dictionary<any> = yield select(getAccountImportsFilters)

  const nonEmptyFilters = filteredFilters(filters)
  const preprocessedFilters = preprocessFilters(nonEmptyFilters)

  const { data }: AxiosResponse<ImportHistoryResponse> = yield call(apiCall, {
    url: '/api/import/history',
    method: 'post',
    params: {
      domainCode: countryCode,
      page: payload.page,
      pageSize: payload.pageSize,
      order: sortParam(sorting),
      search: filter || undefined
    },
    data: _.every(_.values(preprocessedFilters), _.isEmpty) ? {} : preprocessedFilters
  })

  yield put(
    ImportHistoryActions.setData({
      data: data.data.imports,
      page: data.meta.pageNumber,
      totalPages: data.meta.pageCount
    })
  )

  if (data.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'importHistory',
        pagination: {
          current: data.meta.pageNumber,
          pageSize: payload.pageSize,
          total: data.meta.totalCount
        }
      })
    )
  }
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'importHistory') return

  const filter: string = yield select(getAccountImportsFilter)
  const pageSize: number = yield select(getAccountReportsPageSize)

  yield put(
    ImportHistoryActions.fetchData({
      filter,
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'importHistory') return

  const filter: string = yield select(getAccountImportsFilter)
  const { pagination } = payload

  yield put(
    ImportHistoryActions.fetchData({
      filter,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* reloadOnFiltersChange() {
  const filter: string = yield select(getAccountImportsFilter)
  const pageSize: number = yield select(getAccountImportsPageSize)

  yield put(
    ImportHistoryActions.fetchData({
      filter,
      page: 1,
      pageSize
    })
  )
}

export function* downloadExport({ payload }: ReturnType<typeof ImportHistoryActions.downloadExport>) {
  const { exportId } = payload

  yield call(downloadExportViaToken, exportId, 'import')
  return
}

/* istanbul ignore next */
export function* importHistorySagas() {
  yield all([
    takeLatest(getType(ImportHistoryActions.fetchData), withLoading('importHistory', fetchImports)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(ImportHistoryActions.downloadExport), downloadExport),
    takeLatest(getType(ImportHistoryActions.exportImports), exportImports),
    takeLatest(getType(ImportHistoryActions.setFilters), reloadOnFiltersChange),
    takeLatest(getType(ImportHistoryActions.clearFilter), reloadOnFiltersChange)
  ])
}
